import i18next from 'i18next';

import {commonConfig} from './config';
import {escapeHtml} from './html';

export const PLACEMENT_PURCHASE_SINGLE_AD = 'single_ad';
export const PLACEMENT_PURCHASE_CATEGORY_PACKET = 'category_packet';
export const PLACEMENT_PURCHASE_MEGA_PACKET = 'mega_packet';

export const formatMoney = (money) => {
    if (!money) {
        return '-';
    }
    const amount = Number(money.amount);
    if (!Number.isFinite(amount)) {
        return '-';
    }
    const formatted = amount.toLocaleString(undefined, {
        minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
        maximumFractionDigits: 2,
    });
    const currency = commonConfig.currencies[money.currency] || money.currency || '';
    return `${formatted}${currency ? ` ${currency}` : ''}`;
};

const parseLocalDateTime = (value) => {
    if (!value) {
        return null;
    }
    if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? null : value;
    }
    if (typeof value === 'number') {
        const date = new Date(value);
        return Number.isNaN(date.getTime()) ? null : date;
    }

    const rawValue = String(value).trim();
    const localDateTimeMatch = rawValue.match(
        /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?$/
    );

    if (localDateTimeMatch) {
        const [, year, month, day, hour = '0', minute = '0', second = '0'] = localDateTimeMatch;
        const date = new Date(
            Number(year),
            Number(month) - 1,
            Number(day),
            Number(hour),
            Number(minute),
            Number(second)
        );
        return Number.isNaN(date.getTime()) ? null : date;
    }

    const date = new Date(rawValue);
    return Number.isNaN(date.getTime()) ? null : date;
};

const formatLocalDateTimeWithoutSeconds = (value) => {
    const date = parseLocalDateTime(value);
    if (!date) {
        return value ? String(value) : '-';
    }
    const browserLocale = typeof navigator !== 'undefined' ? navigator.language : undefined;
    return new Intl.DateTimeFormat(i18next.language || browserLocale || undefined, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
    }).format(date);
};

export const getPlacementCategoryKey = (category) => [
    category?.account_id ?? '',
    category?.category_id ?? '',
    category?.zone_id ?? '',
].map(String).join('|');

export const getPlacementPacketKey = (category, packet) => [
    category?.account_id ?? '',
    category?.category_id ?? '',
    category?.zone_id ?? '',
    packet?.purchase_kind ?? '',
    packet?.ad_id ?? '',
    packet?.draft_id ?? '',
    packet?.size ?? '',
    packet?.payment_method ?? '',
    packet?.id ?? '',
    packet?.type ?? 'base',
    packet?.packet_type ?? '',
    packet?.is_premium ? 'premium' : 'standard',
    packet?.name ?? '',
    packet?.price?.amount ?? '',
    packet?.price?.currency ?? '',
].map((value) => encodeURIComponent(String(value))).join('|');

const getPlacementPurchaseOptions = (category) => {
    const options = category?.purchase_options || {};
    return [
        ...(options.single_ad_options || []),
        ...(options.category_packet_options || []),
        ...(options.mega_packet_options || []),
    ];
};

export const getReusablePackagePurchaseOptions = (category) => (
    getPlacementPurchaseOptions(category).filter((packet) => (
        packet.purchase_kind !== PLACEMENT_PURCHASE_SINGLE_AD && Number(packet.size || 0) !== 1
    ))
);

export const sortPackets = (packets) => [...(packets || [])].sort((a, b) => (
    Number(a.size || 0) - Number(b.size || 0)
    || Number(a.price?.amount || 0) - Number(b.price?.amount || 0)
    || String(a.name || '').localeCompare(String(b.name || ''))
));

const getPacketSizeOptions = (packets) => (
    [...new Set((packets || []).map((packet) => Number(packet.size || 0)).filter((size) => size > 0))]
        .sort((a, b) => a - b)
);

const getQuantityThresholdSize = (sizeOptions, missingSlots) => (
    sizeOptions.find((size) => size >= Number(missingSlots || 0)) || sizeOptions[sizeOptions.length - 1] || 0
);

const isStartPlacementPacket = (packet) => {
    const name = String(packet?.name || '').toLowerCase();
    const type = String(packet?.packet_type || packet?.type || '').toLowerCase();
    return !packet?.is_premium && type !== 'mega' && !name.includes('premium') && !name.includes('прем');
};

export const getPreferredReusablePlacementPacketForSize = (category, size, paymentMethod = '') => {
    const packets = sortPackets(getReusablePackagePurchaseOptions(category))
        .filter((packet) => Number(packet.size || 0) === Number(size))
        .filter((packet) => !paymentMethod || String(packet.payment_method || '') === String(paymentMethod));
    return packets.find(isStartPlacementPacket) || packets[0] || null;
};

export const getDefaultReusablePlacementPacket = (category) => {
    const packets = sortPackets(getReusablePackagePurchaseOptions(category));
    if (!packets.length) {
        return null;
    }
    const recommended = category?.recommended_package;
    if (recommended) {
        const recommendedKey = getPlacementPacketKey(category, recommended);
        const matchingRecommended = packets.find((packet) => getPlacementPacketKey(category, packet) === recommendedKey);
        if (matchingRecommended) {
            return matchingRecommended;
        }
    }
    const missingSlots = Number(category?.missing_slots || 0);
    const optimalSize = getQuantityThresholdSize(getPacketSizeOptions(packets), missingSlots);
    const preferredOptimal = optimalSize ? getPreferredReusablePlacementPacketForSize(category, optimalSize) : null;
    if (preferredOptimal) {
        return preferredOptimal;
    }
    const coveringPackets = packets.filter((packet) => Number(packet.size || 0) >= missingSlots);
    return coveringPackets.find(isStartPlacementPacket) || coveringPackets[0] || packets[0];
};

const getCompactQuantitySizeOptions = (sizeOptions, optimalSize, selectedSize) => {
    if (!sizeOptions.length || !optimalSize) {
        return sizeOptions.slice(0, 4);
    }
    const optimalIndex = sizeOptions.findIndex((size) => Number(size) === Number(optimalSize));
    if (optimalIndex < 0) {
        return sizeOptions.slice(0, 4);
    }
    const preferred = [];
    if (optimalIndex > 0) {
        preferred.push(sizeOptions[optimalIndex - 1]);
    }
    preferred.push(sizeOptions[optimalIndex]);
    if (optimalIndex + 1 < sizeOptions.length) {
        preferred.push(sizeOptions[optimalIndex + 1]);
    }
    if (optimalIndex + 2 < sizeOptions.length) {
        preferred.push(sizeOptions[optimalIndex + 2]);
    }
    const selected = Number(selectedSize || 0);
    if (selected && !preferred.includes(selected) && sizeOptions.includes(selected)) {
        preferred.push(selected);
    }
    return [...new Set(preferred)].sort((a, b) => a - b);
};

export const formatPlacementPacketPrice = (packet) => (
    packet?.price
        ? `${packet.price_estimated ? '~' : ''}${formatMoney(packet.price)}`
        : i18next.t('ads:placementPlan.priceCalculatedByOlx', {defaultValue: 'Calculated by OLX at purchase'})
);

export const getPacketVariantName = (packet) => {
    const name = packet?.name || '';
    if (packet?.purchase_kind === PLACEMENT_PURCHASE_MEGA_PACKET || String(packet?.packet_type || packet?.type || '').toLowerCase() === 'mega') {
        return i18next.t('ads:placementPlan.megaPackageVariant', {defaultValue: 'Mega'});
    }
    const quoted = name.match(/[ʼ'"]([^ʼ'"]+)[ʼ'"]/);
    if (quoted?.[1]) {
        return quoted[1];
    }
    if (name) {
        return name;
    }
    return i18next.t('ads:placementPlan.packetVariant', {defaultValue: 'Package'});
};

const getMegaCoverageLabels = (packet) => {
    const explicitLabels = (packet?.categories_labels || []).filter(Boolean);
    if (explicitLabels.length) {
        return explicitLabels;
    }
    const namePrefix = String(packet?.name || '').split('/')[0] || '';
    const cleaned = namePrefix
        .replace(/\s+мега\s*$/i, '')
        .replace(/\s+mega\s*$/i, '')
        .trim();
    if (!cleaned) {
        return [];
    }
    return cleaned
        .split(/\s+(?:та|и|і|and|&)\s+/i)
        .map((label) => label.trim())
        .filter(Boolean);
};

export const getPaymentMethodLabel = (account, value) => {
    const method = (account?.payment_methods || []).find((item) => String(item.value || '') === String(value || ''));
    return method?.label || value || '-';
};

const getPackageHeadingLabel = (size) => (
    Number(size) === 1
        ? i18next.t('ads:placementPlan.chooseSingleAd', {defaultValue: 'Choose advert type'})
        : i18next.t('ads:placementPlan.choosePackage', {defaultValue: 'Choose package'})
);

const getCategoryDisplayName = (category, categoryDisplayNames = {}) => {
    const categoryId = String(category?.category_id ?? '');
    return categoryDisplayNames[categoryId]
        || category?.category_name
        || category?.category_id
        || i18next.t('ads:placementPlan.unknownCategory', {defaultValue: 'Unknown category'});
};

const renderBalanceAfterPurchase = (balance, packet) => {
    const price = packet?.price;
    if (!balance || !price || balance.currency !== price.currency) {
        return '-';
    }
    const amount = Number(balance.amount) - Number(price.amount);
    if (!Number.isFinite(amount)) {
        return '-';
    }
    return formatMoney({amount, currency: balance.currency});
};

const renderFinancialPurchasePreview = ({account, selected, paymentMethodsForSize}) => {
    if (!selected) {
        return '';
    }
    const balance = account?.olx_balance || account?.balance;
    const showPaymentMethod = paymentMethodsForSize.length > 1;
    const hasKnownPrice = selected.price && !selected.price_unavailable;
    const balanceBefore = formatMoney(balance);
    const balanceAfter = renderBalanceAfterPurchase(balance, selected);
    return `
        <div class="placement-plan-purchase-preview placement-plan-purchase-preview--financial">
            ${hasKnownPrice ? `
                <div class="placement-plan-purchase-balance-row">
                    <span>${escapeHtml(i18next.t('ads:placementPlan.balance', {defaultValue: 'Balance'}))}</span>
                    <strong>${escapeHtml(balanceBefore)} → ${escapeHtml(balanceAfter)}</strong>
                </div>
            ` : `
                <div class="placement-plan-purchase-note">
                    ${escapeHtml(i18next.t('ads:placementPlan.priceCalculatedByOlxNote', {defaultValue: 'Final price is returned by OLX during purchase.'}))}
                </div>
            `}
            ${showPaymentMethod ? `
                <div>
                    <span>${escapeHtml(i18next.t('ads:placementPlan.paymentMethod', {defaultValue: 'Payment method'}))}</span>
                    <strong>${escapeHtml(getPaymentMethodLabel(account, selected.payment_method))}</strong>
                </div>
            ` : ''}
        </div>
    `;
};

const renderPlacementOptimalIcon = () => `
    <svg class="placement-plan-optimal-icon" xmlns="http://www.w3.org/2000/svg" version="1.1" viewBox="0 0 511.999 511.999" aria-hidden="true" focusable="false">
        <path d="M15.34 224.948c-8.365 0-15.146 6.782-15.146 15.146v222.458c0 8.365 6.782 15.146 15.146 15.146h91.253v-252.75H15.34zM511.805 308.587c0-19.227-10.331-36.087-25.733-45.321a52.504 52.504 0 0 0 9.362-29.985c0-29.113-23.686-52.799-52.799-52.799H331.177c3.416-15.48 8.088-38.709 11.341-63.026 8.466-63.281 2.68-98.376-18.207-110.445-13.022-7.523-27.062-9.049-39.534-4.301-9.635 3.67-22.647 12.693-30.062 34.838l-29.294 76.701c-14.851 36.677-60.33 75.182-88.535 96.473V484.4C189.16 502.716 243.708 512 299.358 512h121.446c29.113 0 52.799-23.686 52.799-52.799a52.481 52.481 0 0 0-7.93-27.804c17.601-8.572 29.759-26.646 29.759-47.503a52.504 52.504 0 0 0-9.362-29.985c15.404-9.234 25.735-26.095 25.735-45.322z" fill="currentColor"></path>
    </svg>
`;

export const renderReusablePackageSelector = ({
    account,
    category,
    selectedPacket = null,
    categoryDisplayNames = {},
    expandedPackageSizeKeysSet = null,
    showNeededLabel = true,
    showOptimalQuantityHint = true,
    showNearestQuantityNote = true,
    showPurchasePreview = true,
    purchasePreviewMode = 'full',
    emptyMessage = '',
} = {}) => {
    const allOptions = sortPackets(getReusablePackagePurchaseOptions(category));
    if (!allOptions.length) {
        return `<span class="placement-plan-muted">${escapeHtml(emptyMessage || i18next.t('ads:placementPlan.noAvailableReusablePackets', {defaultValue: 'No reusable packages are available. Use individual placement on adverts below.'}))}</span>`;
    }

    const selected = selectedPacket || getDefaultReusablePlacementPacket(category);
    const selectedSize = Number(selected?.size || allOptions[0]?.size || 0);
    const missingSlots = Number(category?.missing_slots || 0);
    const categoryKey = getPlacementCategoryKey(category);
    const sizeOptions = getPacketSizeOptions(allOptions);
    const thresholdSize = getQuantityThresholdSize(sizeOptions, missingSlots);
    const isAllSizesExpanded = expandedPackageSizeKeysSet?.has(categoryKey);
    const visibleSizeOptions = isAllSizesExpanded
        ? sizeOptions
        : getCompactQuantitySizeOptions(sizeOptions, thresholdSize, selectedSize);
    const hasHiddenSizes = visibleSizeOptions.length < sizeOptions.length;
    const selectedBelowNeeded = showNeededLabel && thresholdSize && selectedSize < thresholdSize;
    const isNearestAvailable = showNeededLabel && thresholdSize && missingSlots > 0 && thresholdSize !== missingSlots && (thresholdSize - missingSlots) > 1;
    const packetsForSize = allOptions.filter((packet) => Number(packet.size || 0) === selectedSize);
    const paymentMethodsForSize = [...new Set(packetsForSize.map((packet) => String(packet.payment_method || '')).filter(Boolean))];
    const selectedPacketKey = selected ? getPlacementPacketKey(category, selected) : '';
    const selectedPaymentMethod = String(selected?.payment_method || paymentMethodsForSize[0] || '');
    const categoryDisplayName = getCategoryDisplayName(category, categoryDisplayNames);
    const visiblePacketsForSize = packetsForSize
        .filter((packet) => paymentMethodsForSize.length <= 1 || String(packet.payment_method || '') === selectedPaymentMethod);

    return `
        <div class="placement-plan-package-selector ${showOptimalQuantityHint ? '' : 'placement-plan-package-selector--no-optimal-hint'}" data-placement-category-key="${escapeHtml(categoryKey)}">
            <div class="placement-plan-selector-heading">
                <div class="placement-plan-selector-label">${escapeHtml(i18next.t('ads:placementPlan.quantitySelector', {defaultValue: 'Number of adverts'}))}</div>
                ${showNeededLabel ? `<div class="placement-plan-needed-label">${escapeHtml(i18next.t('ads:placementPlan.neededQuantity', {defaultValue: 'Needed: {{count}}', count: missingSlots}))}</div>` : ''}
            </div>
            <div class="placement-plan-size-options">
                ${visibleSizeOptions.map((size) => {
                    const sizeClasses = [
                        'placement-plan-size-option',
                        size === selectedSize ? 'active' : '',
                        showNeededLabel && thresholdSize && size === thresholdSize ? 'is-threshold' : '',
                        showNeededLabel && thresholdSize && size < thresholdSize ? 'is-before-threshold' : '',
                    ].filter(Boolean).join(' ');
                    const isOptimal = showNeededLabel && thresholdSize && size === thresholdSize;
                    const shouldShowOptimalHint = showOptimalQuantityHint && isOptimal;
                    const title = isOptimal
                        ? i18next.t('ads:placementPlan.coversWholeList', {defaultValue: 'Covers the whole list'})
                        : (showNeededLabel && thresholdSize && size < thresholdSize
                            ? i18next.t('ads:placementPlan.belowNeededQuantity', {defaultValue: 'Not enough for {{count}} adverts', count: missingSlots})
                            : '');
                    return `
                        <span class="placement-plan-size-option-wrap ${shouldShowOptimalHint ? 'is-optimal' : ''}">
                            ${shouldShowOptimalHint ? `
                                <span class="placement-plan-size-optimal-label" title="${escapeHtml(i18next.t('ads:placementPlan.optimalQuantityShort', {defaultValue: 'Optimal'}))}">
                                    ${renderPlacementOptimalIcon()}
                                </span>
                            ` : ''}
                            <button
                                type="button"
                                class="${escapeHtml(sizeClasses)}"
                                data-placement-category-key="${escapeHtml(categoryKey)}"
                                data-placement-size="${escapeHtml(size)}"
                                title="${escapeHtml(title)}"
                            >
                                ${escapeHtml(size)}
                            </button>
                        </span>
                    `;
                }).join('')}
                ${hasHiddenSizes || isAllSizesExpanded ? `
                    <button
                        type="button"
                        class="placement-plan-size-toggle"
                        data-placement-category-key="${escapeHtml(categoryKey)}"
                        aria-expanded="${isAllSizesExpanded ? 'true' : 'false'}"
                    >
                        ${escapeHtml(isAllSizesExpanded
                            ? i18next.t('ads:placementPlan.hideAllQuantities', {defaultValue: 'Hide'})
                            : i18next.t('ads:placementPlan.showAllQuantities', {defaultValue: 'More'}))}
                    </button>
                ` : ''}
            </div>
            ${showNearestQuantityNote && isNearestAvailable ? `
                <div class="placement-plan-quantity-note">
                    ${escapeHtml(i18next.t('ads:placementPlan.nearestOlxQuantity', {defaultValue: 'Closest available OLX package'}))}
                </div>
            ` : ''}
            ${selectedBelowNeeded ? `
                <div class="placement-plan-inline-warning">
                    ${escapeHtml(i18next.t('ads:placementPlan.packageDoesNotCoverAll', {defaultValue: 'This package will not cover all adverts.'}))}
                </div>
            ` : ''}
            <div class="placement-plan-package-header">
                <span>${escapeHtml(getPackageHeadingLabel(selectedSize))}</span>
                ${paymentMethodsForSize.length > 1 ? `
                    <label class="placement-plan-package-select">
                        <span>${escapeHtml(i18next.t('ads:placementPlan.paymentMethod', {defaultValue: 'Payment method'}))}</span>
                        <select class="form-select og-select placement-plan-payment-method-select" data-placement-category-key="${escapeHtml(categoryKey)}" data-placement-size="${escapeHtml(selectedSize)}">
                            ${paymentMethodsForSize.map((method) => `
                                <option value="${escapeHtml(method)}" ${method === selectedPaymentMethod ? 'selected' : ''}>${escapeHtml(getPaymentMethodLabel(account, method))}</option>
                            `).join('')}
                        </select>
                    </label>
                ` : ''}
            </div>
            <div class="placement-plan-package-options">
                ${visiblePacketsForSize.map((packet) => {
                    const packetKey = getPlacementPacketKey(category, packet);
                    const isSelected = packetKey === selectedPacketKey;
                    const megaLabels = getMegaCoverageLabels(packet);
                    return `
                        <button
                            type="button"
                            class="placement-plan-package-card ${isSelected ? 'active' : ''}"
                            data-placement-category-key="${escapeHtml(categoryKey)}"
                            data-placement-packet-key="${escapeHtml(packetKey)}"
                        >
                            <span>${escapeHtml(getPacketVariantName(packet))}</span>
                            <strong>${escapeHtml(formatPlacementPacketPrice(packet))}</strong>
                            ${packet.purchase_kind === PLACEMENT_PURCHASE_MEGA_PACKET ? `
                                <em>${escapeHtml(megaLabels.length
                                    ? megaLabels.join(', ')
                                    : i18next.t('ads:placementPlan.megaCoverage', {defaultValue: 'Works in multiple categories'}))}</em>
                            ` : ''}
                            ${packet.price_unavailable ? `<em>${escapeHtml(i18next.t('ads:placementPlan.priceCalculatedByOlxNote', {defaultValue: 'Final price is returned by OLX during purchase.'}))}</em>` : ''}
                            ${packet.price_estimated ? `<em>${escapeHtml(i18next.t('ads:placementPlan.priceEstimatedNote', {defaultValue: 'Estimated. OLX calculates the final price during purchase.'}))}</em>` : ''}
                            <small>${escapeHtml(isSelected
                                ? i18next.t('ads:placementPlan.selectedPackage', {defaultValue: 'Selected'})
                                : i18next.t('ads:placementPlan.selectPackage', {defaultValue: 'Select'}))}</small>
                        </button>
                    `;
                }).join('')}
            </div>
            ${showPurchasePreview && selected && purchasePreviewMode === 'financial' ? renderFinancialPurchasePreview({
                account,
                selected,
                paymentMethodsForSize,
            }) : ''}
            ${showPurchasePreview && selected && purchasePreviewMode === 'full' ? `
                <div class="placement-plan-purchase-preview">
                    <div><span>${escapeHtml(i18next.t('ads:placementPlan.purchaseKind', {defaultValue: 'Placement type'}))}</span><strong>${escapeHtml(getPackageHeadingLabel(selected.size))}</strong></div>
                    <div><span>${escapeHtml(i18next.t('ads:placementPlan.purchaseAccount', {defaultValue: 'Account'}))}</span><strong>${escapeHtml(account?.account_name || account?.name || account?.account_id || account?.id || '-')}</strong></div>
                    <div><span>${escapeHtml(i18next.t('ads:placementPlan.purchaseCategory', {defaultValue: 'Category'}))}</span><strong>${escapeHtml(categoryDisplayName)}</strong></div>
                    <div><span>${escapeHtml(i18next.t('ads:placementPlan.purchaseQuantity', {defaultValue: 'Quantity'}))}</span><strong>${escapeHtml(selected.size || '-')}</strong></div>
                    <div><span>${escapeHtml(i18next.t('ads:placementPlan.purchasePackage', {defaultValue: 'Package'}))}</span><strong>${escapeHtml(getPacketVariantName(selected))}</strong></div>
                    <div><span>${escapeHtml(i18next.t(selected.price_estimated ? 'ads:placementPlan.purchaseEstimatedPrice' : 'ads:placementPlan.purchasePrice', {defaultValue: selected.price_estimated ? 'Estimated price' : 'Price'}))}</span><strong>${escapeHtml(formatPlacementPacketPrice(selected))}</strong></div>
                    <div><span>${escapeHtml(i18next.t('ads:placementPlan.paymentMethod', {defaultValue: 'Payment method'}))}</span><strong>${escapeHtml(getPaymentMethodLabel(account, selected.payment_method))}</strong></div>
                    <div><span>${escapeHtml(i18next.t('ads:placementPlan.balanceBefore', {defaultValue: 'Balance before'}))}</span><strong>${escapeHtml(formatMoney(account?.olx_balance || account?.balance))}</strong></div>
                    <div><span>${escapeHtml(i18next.t('ads:placementPlan.balanceAfter', {defaultValue: 'Balance after'}))}</span><strong>${escapeHtml(renderBalanceAfterPurchase(account?.olx_balance || account?.balance, selected))}</strong></div>
                </div>
            ` : ''}
        </div>
    `;
};

export const renderBoughtPacketsList = (packets = []) => {
    if (!packets.length) {
        return `<div class="olx-placement-empty">${escapeHtml(i18next.t('ads:placementPlan.noBoughtPackets', {defaultValue: 'No bought OLX packages.'}))}</div>`;
    }
    const getBoughtPacketCategoryLabels = (packet) => {
        const labels = packet.category_labels || packet.categories_labels || [];
        if (Array.isArray(labels) && labels.filter(Boolean).length) {
            return labels.filter(Boolean).map(String);
        }
        const ids = packet.category_ids || packet.categories_ids || [];
        if (Array.isArray(ids) && ids.filter(Boolean).length) {
            return ids.filter(Boolean).map((id) => `#${id}`);
        }
        if (packet.category_id) {
            return [`#${packet.category_id}`];
        }
        return [];
    };
    const getBoughtPacketCategoryFallback = () => (
        i18next.t('ads:placementAccounts.categoryNotSpecified', {defaultValue: 'Category not specified'})
    );
    const renderBoughtPacketCategories = (packet, index) => {
        const categoryLabels = getBoughtPacketCategoryLabels(packet);
        const hasCategories = categoryLabels.length > 0;
        const fallback = getBoughtPacketCategoryFallback();
        const fullLabel = hasCategories ? categoryLabels.join(', ') : fallback;
        const summary = hasCategories ? categoryLabels.slice(0, 2).join(', ') : fallback;
        const shouldCollapse = hasCategories && (categoryLabels.length > 2 || fullLabel.length > 72);
        const collapsedLabel = i18next.t('ads:placementAccounts.showCategoriesFull', {defaultValue: 'Show full'});
        const encodedLabels = encodeURIComponent(JSON.stringify(categoryLabels));

        return `
            <div class="olx-placement-bought-packet__categories">
                <span>${escapeHtml(i18next.t('ads:placementAccounts.includedCategories', {defaultValue: 'Included categories'}))}</span>
                <div class="olx-placement-bought-packet__categories-summary">
                    <strong title="${escapeHtml(fullLabel)}">${escapeHtml(summary)}</strong>
                    ${shouldCollapse ? `
                        <button
                            type="button"
                            class="olx-placement-bought-packet__categories-toggle"
                            aria-haspopup="dialog"
                            data-category-labels="${escapeHtml(encodedLabels)}"
                        >
                            ${escapeHtml(collapsedLabel)}
                        </button>
                    ` : ''}
                </div>
            </div>
        `;
    };
    const getBoughtPacketLocationLabel = (packet) => (
        packet.location_name
        || packet.zone_name
        || (packet.zone_id ? `#${packet.zone_id}` : '')
        || i18next.t('ads:placementAccounts.noLocation', {defaultValue: 'No location'})
    );
    const getBoughtPacketActiveTo = (packet) => (
        formatLocalDateTimeWithoutSeconds(
            packet.active_to
            || packet.valid_to
            || packet.expires_at
            || packet.expiration_date
        )
    );
    const formatBoughtPacketPrice = (packet) => (
        packet.price ? formatMoney(packet.price) : '-'
    );
    return `
        <div class="olx-placement-bought-packets">
            ${packets.map((packet, index) => `
                <div class="olx-placement-bought-packet">
                    <div class="olx-placement-bought-packet__main">
                        <strong>${escapeHtml(getPacketVariantName(packet))}</strong>
                        <span>${escapeHtml(packet.name || packet.label || '')}</span>
                        ${renderBoughtPacketCategories(packet, index)}
                        <em>${escapeHtml(getBoughtPacketLocationLabel(packet))}</em>
                    </div>
                    <div>
                        <span>${escapeHtml(i18next.t('ads:placementPlan.purchaseQuantity', {defaultValue: 'Quantity'}))}</span>
                        <strong>${escapeHtml(`${packet.left ?? '-'} / ${packet.size ?? '-'}`)}</strong>
                    </div>
                    <div>
                        <span>${escapeHtml(i18next.t('ads:placementAccounts.activeTo', {defaultValue: 'Active to'}))}</span>
                        <strong>${escapeHtml(getBoughtPacketActiveTo(packet))}</strong>
                    </div>
                </div>
            `).join('')}
        </div>
    `;
};
